import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import CommunityPreview from '../CommunityPreview';
import { isDisallowedImageUrl } from '../../utils/imageUrlSafety';
import { API, getToken } from './adminApi';
import CommunityVideoSection from './CommunityVideoSection';
import './ManageCommunitiesTab.css';

const DND_ITEM_TYPE = 'community-pic';

// Every field CommunityPreview's edit mode can render an EditableField for.
// Keep this in sync with the backend's PATCH /api/admin/communities/:id whitelist.
const EDITABLE_FIELDS = [
  'title', 'topLevelSummary', 'heroTagline', 'homePageTagline',
  'location', 'type', 'eligibilityDescription', 'mainDescription',
];

// Defends against a known data bug where some communities were saved with a
// single comma-joined pics[0] string instead of separate array entries.
const normalizePics = (pics) =>
  (pics || []).flatMap(p => (typeof p === 'string' && p.includes(',') ? p.split(',').map(s => s.trim()).filter(Boolean) : [p]));

const buildEditState = (c) => {
  const state = { _id: c._id, pics: normalizePics(c.pics), databaseRef: c.databaseRef || '', showNoCommitmentSignup: c.showNoCommitmentSignup === true };
  for (const key of EDITABLE_FIELDS) state[key] = c[key] || '';
  return state;
};

const SLUG_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/i;

const FIELD_LABELS = {
  title: 'Title',
  topLevelSummary: 'Top-level summary',
  heroTagline: 'Hero tagline',
  homePageTagline: 'Homepage tagline',
  location: 'Location',
  type: 'Type',
  eligibilityDescription: 'Eligibility description',
  mainDescription: 'Main description',
};

const DraggableThumb = ({ pic, index, isHero, movePic, onDelete }) => {
  const [, drag] = useDrag(() => ({ type: DND_ITEM_TYPE, item: { index } }), [index]);
  const [, drop] = useDrop(() => ({
    accept: DND_ITEM_TYPE,
    hover(item) {
      if (item.index === index) return;
      movePic(item.index, index);
      item.index = index;
    },
  }), [index, movePic]);

  return (
    <div ref={node => drag(drop(node))} className="amc-pic-square">
      {isHero && <div className="amc-hero-badge">Hero</div>}
      <div className="amc-pic-delete" onClick={() => onDelete(index)}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
      <img src={pic} alt={`Community ${index + 1}`} className="amc-pic-img" />
    </div>
  );
};

const ManageCommunitiesTab = () => {
  const [communities, setCommunities] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [editState, setEditState] = useState(null);
  const [originalState, setOriginalState] = useState(null);
  const [editorTab, setEditorTab] = useState('pictures');
  const [previewEditMode, setPreviewEditMode] = useState(true);
  const [newPicUrl, setNewPicUrl] = useState('');
  const [picUrlError, setPicUrlError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // Archiving several at once: the ticked communities, and which list is
  // shown ('active' or 'archived').
  const [checked, setChecked] = useState(() => new Set());
  const [listView, setListView] = useState('active');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  useEffect(() => { fetchCommunities(); }, []);

  const fetchCommunities = async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await fetch(`${API}/communities?includeArchived=1`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load communities');
      setCommunities(data);
    } catch (err) {
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  };

  const isDirty = editState && originalState && JSON.stringify(editState) !== JSON.stringify(originalState);

  const selectCommunity = (c) => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them and switch community?')) return;
    const next = buildEditState(c);
    setEditState(next);
    setOriginalState(next);
    setSelectedId(c._id);
    setEditorTab('pictures');
    setPreviewEditMode(true);
    setSaveMsg('');
    setPicUrlError('');
    setNewPicUrl('');
  };

  const backToList = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them and go back to the list?')) return;
    setSelectedId(null);
    setEditState(null);
    setOriginalState(null);
  };

  const handleFieldSave = (key, val) => setEditState(prev => ({ ...prev, [key]: val }));

  const movePic = (from, to) => {
    setEditState(prev => {
      const pics = [...prev.pics];
      const [moved] = pics.splice(from, 1);
      pics.splice(to, 0, moved);
      return { ...prev, pics };
    });
  };

  const deletePic = (index) => {
    setEditState(prev => ({ ...prev, pics: prev.pics.filter((_, i) => i !== index) }));
  };

  const addPicUrl = () => {
    const url = newPicUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      setPicUrlError('Must be a full http(s):// image URL.');
      return;
    }
    if (isDisallowedImageUrl(url)) {
      setPicUrlError('Images of or relating to children are not permitted.');
      return;
    }
    setPicUrlError('');
    setEditState(prev => ({ ...prev, pics: [...prev.pics, url] }));
    setNewPicUrl('');
  };

  const handleSave = async () => {
    if (editState.databaseRef !== originalState.databaseRef) {
      if (!SLUG_PATTERN.test(editState.databaseRef)) {
        setSaveMsg('Error: URL can only contain letters, numbers, dashes and underscores.');
        return;
      }
      if (!window.confirm(
        `Change the live URL from /${originalState.databaseRef} to /${editState.databaseRef}?\n\n` +
        `The old URL will keep working and redirect visitors to the new one.`
      )) return;
    }

    setSaving(true);
    setSaveMsg('');
    try {
      const token = await getToken();
      const payload = { pics: editState.pics, showNoCommitmentSignup: editState.showNoCommitmentSignup };
      for (const key of EDITABLE_FIELDS) payload[key] = editState[key];
      if (editState.databaseRef !== originalState.databaseRef) payload.databaseRef = editState.databaseRef;
      const res = await fetch(`${API}/admin/communities/${editState._id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setEditState(prev => ({ ...prev, databaseRef: data.community.databaseRef }));
      setOriginalState({ ...editState, databaseRef: data.community.databaseRef });
      setCommunities(prev => prev.map(c => c._id === editState._id ? { ...c, ...data.community } : c));
      setSaveMsg('✓ Saved');
    } catch (err) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const archivedCount = communities.filter(c => c.archived).length;
  const filtered = communities.filter(c => {
    if (listView === 'archived' ? !c.archived : c.archived) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.title || '').toLowerCase().includes(q) || (c.location || '').toLowerCase().includes(q);
  });

  const selectedCommunity = communities.find(c => c._id === selectedId);

  const changedFields = editState && originalState
    ? EDITABLE_FIELDS.filter(k => editState[k] !== originalState[k])
    : [];
  const picsChanged = editState && originalState &&
    JSON.stringify(editState.pics) !== JSON.stringify(originalState.pics);

  const previewFields = editState ? {
    ...editState,
    connectionReasons: (selectedCommunity?.connectionReasons || []).join(','),
  } : null;

  const editorTabs = [
    { id: 'pictures', label: 'Pictures' },
    { id: 'preview', label: 'Preview' },
    { id: 'update', label: 'Update' },
  ];

  // Full-page tabbed editor — shown once a community is selected. Kept as
  // its own dedicated layout (not shared with CommunityBuilder) so this
  // admin tool isn't coupled to the AI-builder component.
  if (editState) {
    return (
      <div className="amc-editor-root">
        <div className="amc-editor-tabbar">
          <button className="amc-editor-back" onClick={backToList}>← All communities</button>
          {editorTabs.map(t => (
            <button
              key={t.id}
              className={`amc-editor-tab ${editorTab === t.id ? 'amc-editor-tab--active' : ''}`}
              onClick={() => setEditorTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {saveMsg && <span className="amc-save-msg">{saveMsg}</span>}
        </div>

        <div className="amc-editor-title-strip">
          <strong>{editState.title || 'Untitled community'}</strong>
          {isDirty && <span className="amc-unsaved-badge">Unsaved changes</span>}
        </div>

        <div className="amc-editor-content">
          {editorTab === 'pictures' && (
            <div className="amc-pictures">
              <p className="amc-hint">Drag to reorder — the first picture is used as the hero image everywhere.</p>
              <DndProvider backend={HTML5Backend}>
                <div className="amc-pic-grid">
                  {editState.pics.map((pic, i) => (
                    <DraggableThumb key={pic} pic={pic} index={i} isHero={i === 0} movePic={movePic} onDelete={deletePic} />
                  ))}
                </div>
              </DndProvider>
              <div className="amc-add-pic-row">
                <input
                  className="amc-add-pic-input"
                  placeholder="Paste an image URL…"
                  value={newPicUrl}
                  onChange={e => setNewPicUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addPicUrl(); }}
                />
                <button className="as-btn as-btn--approve" onClick={addPicUrl}>+ Add</button>
              </div>
              {picUrlError && <div className="as-error">{picUrlError}</div>}
            </div>
          )}

          {editorTab === 'preview' && (
            <div className="amc-preview-scroll">
              <div className="amc-preview-mode-bar">
                <a href={`/${selectedCommunity?.databaseRef}`} target="_blank" rel="noopener noreferrer" className="amc-live-link">🔗 Open live page</a>
                <div style={{ flex: 1 }} />
                <button
                  className={`amc-preview-mode-btn ${previewEditMode ? 'amc-preview-mode-btn--active' : ''}`}
                  onClick={() => setPreviewEditMode(true)}
                >✏️ Edit mode</button>
                <button
                  className={`amc-preview-mode-btn ${!previewEditMode ? 'amc-preview-mode-btn--active' : ''}`}
                  onClick={() => setPreviewEditMode(false)}
                >👁 Final view</button>
              </div>
              <CommunityPreview
                fields={previewFields}
                images={editState.pics.map(url => ({ url }))}
                leader={null}
                vetting={null}
                onFieldChange={previewEditMode ? handleFieldSave : null}
              />
            </div>
          )}

          {editorTab === 'update' && (
            <div className="amc-update-pane">
              <h3>Community URL</h3>
              <p className="amc-hint">
                theinterconnector.com/<strong>{editState.databaseRef}</strong> — changing this
                keeps the old URL working; it'll redirect visitors to the new one.
              </p>
              <div className="amc-slug-row">
                <span className="amc-slug-prefix">theinterconnector.com/</span>
                <input
                  className="amc-slug-input"
                  value={editState.databaseRef}
                  onChange={e => handleFieldSave('databaseRef', e.target.value)}
                />
              </div>

              <h3 style={{ marginTop: '1.5rem' }}>Sign-up page</h3>
              <label className="amc-hint" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editState.showNoCommitmentSignup}
                  onChange={e => setEditState(prev => ({ ...prev, showNoCommitmentSignup: e.target.checked }))}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  Show the <strong>No commitment</strong> option — "Automatically delete my account after 3 months
                  if I receive no suggestions I'm interested in"
                </span>
              </label>

              <CommunityVideoSection
                community={communities.find(c => c._id === editState._id) || editState}
                onSaved={(saved) => setCommunities(prev => prev.map(c => (c._id === editState._id ? { ...c, ...saved } : c)))}
              />

              <h3 style={{ marginTop: '1.5rem' }}>Review changes</h3>
              {!isDirty ? (
                <p className="amc-hint">No unsaved changes.</p>
              ) : (
                <ul className="amc-changed-list">
                  {changedFields.map(k => (
                    <li key={k}><strong>{FIELD_LABELS[k] || k}</strong> changed</li>
                  ))}
                  {picsChanged && <li><strong>Pictures</strong> changed</li>}
                  {editState.showNoCommitmentSignup !== originalState.showNoCommitmentSignup && (
                    <li><strong>No commitment option</strong> turned {editState.showNoCommitmentSignup ? 'on' : 'off'}</li>
                  )}
                  {editState.databaseRef !== originalState.databaseRef && (
                    <li><strong>URL</strong> changed to /{editState.databaseRef}</li>
                  )}
                </ul>
              )}
              <button className="as-btn as-btn--approve" disabled={!isDirty || saving} onClick={handleSave}>
                {saving ? 'Saving…' : isDirty ? '💾 Save changes' : '✓ Saved'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const toggleChecked = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const switchView = (v) => { setListView(v); setChecked(new Set()); setBulkMsg(''); };
  const allShownChecked = filtered.length > 0 && filtered.every(c => checked.has(c._id));

  // Archive (or bring back) the ticked communities.
  const setArchived = async (archived) => {
    const ids = [...checked];
    if (!ids.length) return;
    const names = communities.filter(c => checked.has(c._id)).map(c => c.title);
    const question = archived
      ? `Archive ${ids.length} ${ids.length === 1 ? 'community' : 'communities'}?\n\n${names.join('\n')}\n\nThey'll disappear from the website (list, pages and joining). Members, chats and data are kept, and you can unarchive them any time.`
      : `Bring back ${ids.length} ${ids.length === 1 ? 'community' : 'communities'}?\n\n${names.join('\n')}`;
    if (!window.confirm(question)) return;
    setBulkBusy(true);
    setBulkMsg('');
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/communities/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids, archived }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed');
      setCommunities(prev => prev.map(c => (checked.has(c._id) ? { ...c, archived, archivedAt: archived ? new Date().toISOString() : null } : c)));
      setChecked(new Set());
      setBulkMsg(`✓ ${archived ? 'Archived' : 'Brought back'} ${data.updated} ${data.updated === 1 ? 'community' : 'communities'}.`);
    } catch (err) {
      setBulkMsg(`Couldn't ${archived ? 'archive' : 'unarchive'}: ${err.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Picker screen — shown when no community is selected yet.
  return (
    <div className="amc-root">
      {listError && <div className="as-error">{listError}</div>}
      <div className="amc-bulk-bar">
        <button type="button" className={`as-filter-tab${listView === 'active' ? ' as-filter-tab--active' : ''}`} onClick={() => switchView('active')}>
          Active ({communities.length - archivedCount})
        </button>
        <button type="button" className={`as-filter-tab${listView === 'archived' ? ' as-filter-tab--active' : ''}`} onClick={() => switchView('archived')}>
          Archived ({archivedCount})
        </button>
        <label className="amc-bulk-all">
          <input
            type="checkbox"
            checked={allShownChecked}
            onChange={() => setChecked(allShownChecked ? new Set() : new Set(filtered.map(c => c._id)))}
          />
          Select all shown
        </label>
        {checked.size > 0 && (
          <>
            <button type="button" className="as-btn as-btn--approve" disabled={bulkBusy} onClick={() => setArchived(listView !== 'archived')}>
              {bulkBusy ? 'Saving…' : listView === 'archived' ? `↩ Unarchive ${checked.size}` : `🗄 Archive ${checked.size}`}
            </button>
            <button type="button" className="as-filter-tab" onClick={() => setChecked(new Set())}>Clear</button>
          </>
        )}
        {bulkMsg && <span className="amc-bulk-msg">{bulkMsg}</span>}
      </div>
      <input
        className="amc-search"
        placeholder="Search communities…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      {listLoading ? (
        <div className="as-loading">Loading…</div>
      ) : (
        <div className="amc-comm-list amc-comm-list--picker">
          {filtered.map(c => (
            <div
              key={c._id}
              className={`amc-comm-item${checked.has(c._id) ? ' amc-comm-item--checked' : ''}${c.archived ? ' amc-comm-item--archived' : ''}`}
              onClick={() => selectCommunity(c)}
            >
              <input
                type="checkbox"
                className="amc-comm-check"
                checked={checked.has(c._id)}
                onClick={e => e.stopPropagation()}
                onChange={() => toggleChecked(c._id)}
                aria-label={`Select ${c.title}`}
              />
              {c.pics?.[0] ? (
                <img src={c.pics[0]} alt="" className="amc-comm-thumb" />
              ) : (
                <div className="amc-comm-thumb amc-comm-thumb--empty" />
              )}
              <div className="amc-comm-info">
                <div className="amc-comm-title">{c.title}</div>
                <div className="amc-comm-meta">
                  {c.location}
                  {c.archived && <span className="amc-archived-badge">archived{c.archivedAt ? ` ${new Date(c.archivedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</span>}
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="as-empty">No communities found.</div>}
        </div>
      )}
    </div>
  );
};

export default ManageCommunitiesTab;
